/**
 * Camera Registry Page (Phase 30 Wave 4 chunk 30.4.2)
 *
 * Super-admin-only cross-org camera registry. Hosts the existing
 * <CameraAssignmentPanel /> previously embedded in /settings/cameras.
 *
 * Access:
 * - Backend routes (`/api/cameras/alta/*`) are super_admin-gated via
 *   requireSuperAdmin middleware (chunk 30.4.0).
 * - Sidebar nav entry is hidden below level 6 by DashboardLayout filtering
 *   (chunk 30.4.1).
 * - This page additionally guards client-side: a manager/admin who
 *   types the URL is redirected to /dashboard.
 */

import { Navigate } from 'react-router-dom';
import { Server } from 'lucide-react';
import { CameraAssignmentPanel } from '../../../components/cameras/CameraAssignmentPanel';
import { useRoleLevel } from '../../../lib/hooks/useRoleLevel';
import { useCurrentUser } from '../../../hooks/useUsers';
import { useDocumentTitle } from '../../../lib/hooks/useDocumentTitle';

export function CameraRegistryPage() {
  useDocumentTitle('Camera Registry');

  // While /api/me is loading, level === 0; we render a hold state instead of
  // either the panel (which would fire cross-org queries) or the redirect
  // (which would race the loading state on first paint).
  const { isLoading: meLoading } = useCurrentUser();
  const { level } = useRoleLevel();

  if (meLoading) {
    return null;
  }

  if (level < 6) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-4">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-guild-mint/50">
            <Server size={24} className="text-guild-green" />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-gray-900">
              Camera Registry
            </h1>
            <p className="mt-0.5 text-sm text-gray-500">
              Cross-org assignment of Alta cameras to customer organizations
            </p>
          </div>
        </div>
      </div>

      <CameraAssignmentPanel />
    </div>
  );
}

export default CameraRegistryPage;
